import { useEffect, useMemo, useState } from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';
import { getCookie } from '@zooplus/commons-lang';
import { Recommendation } from '@zooplus/recommendations-component/dist/es/src/api/recommendation-api/generated/api';

import { getAppConfig } from '@config/get-app-config';
import { logError } from '@services/sentry-service';
import {
  RECOMMENDATION_API_TIMEOUT,
  RECOMMENDATION_API_VERSION,
  CHECKOUT_STATE_PROJECT_ID,
} from '@utilities/constants';
import { fetchWithTimeout } from '@utilities/http-client';

interface RecommendationObj {
  caption: string;
  recommendations: Recommendation[];
}

type HttpHeaders = {
  [key: string]: string | undefined;
  Authorization?: string;
};

const useFetchRecommendations = (
  pageType: string,
  cartProducts?: string,
  cartArticles?: string,
  headers?: HttpHeaders,
) => {
  const [recommendationsList, setRecommendationsList] = useState<RecommendationObj[]>([]);

  const {
    site: { id, language },
  } = getAppConfig();
  const { checkoutState, isFetching } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { customer: { customerId = '' } = {} } = checkoutState || {};

  const userId = getCookie('zt_id');

  const shopLanguage = useMemo(() => {
    const languageMap: { [key: string]: string } = { nb: 'no' };
    return languageMap[language] || language;
  }, [language]);

  const fetchRecommendations = async () => {
    const queryParams = {
      ...(userId ? { user_id: userId } : {}),
      ...(cartProducts ? { cart_products: cartProducts, product_id: cartProducts } : {}),
      ...(cartArticles ? { article_id: cartArticles } : {}),
      ...(customerId ? { customer_id: customerId.toString() } : {}),
    };
    const queryParamsString = new URLSearchParams(queryParams).toString();

    const url = `/zootopia-recommender/api/${RECOMMENDATION_API_VERSION}/sites/${id}/${shopLanguage}/pages/${pageType}/recommendations?${queryParamsString}`;

    try {
      const result = await fetchWithTimeout(url, RECOMMENDATION_API_TIMEOUT, headers);
      if (result.status === 200) {
        const json = await result.json();
        setRecommendationsList(json);
      }
    } catch (err) {
      logError(err instanceof Error ? err : new Error('Unknown error during fetch'));
    }
  };

  useEffect(() => {
    if (!isFetching) {
      fetchRecommendations();
    }
  }, [isFetching, cartProducts]);

  return { recommendationsList };
};

export default useFetchRecommendations;
